import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calculator, TrendingUp, Crown, Search, Clock, BarChart3, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ResultCard } from '../components/ResultCard';
import { getUserStats, decodeSingle, formatKenmerk, cleanKenmerk } from '../utils/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [usage, setUsage] = useState(null);
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, usageData] = await Promise.all([
          getUserStats(),
          axios.get(`${API_URL}/api/usage`).then(res => res.data).catch(() => null)
        ]);
        setStats(statsData);
        setUsage(usageData);
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, []);

  const handleDecode = async (e) => {
    e.preventDefault();
    const kenmerk = cleanKenmerk(input);
    if (kenmerk.length !== 16) return;

    setLoading(true);
    try {
      const data = await decodeSingle(kenmerk, language);
      setResult(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    
    let text;
    if (result.valid) {
      const taxName = result.belastingtype?.nl || 'Belasting';
      let period = result.tijdvak || '';
      if (period && !period.startsWith('Q') && period !== '-' && period.length > 3) {
        period = period.substring(0, 3).toLowerCase();
      }
      const parts = [];
      parts.push(taxName);
      if (period && period !== '-') {
        parts.push(period);
      }
      if (result.jaar) {
        parts.push(result.jaar);
      }
      text = parts.join(' ');
    } else {
      text = result.kenmerk;
    }
    
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      textArea.remove();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const planColors = {
    free: 'bg-slate-100 text-slate-600',
    pro: 'bg-accent/10 text-accent',
    team: 'bg-purple-100 text-purple-600',
    admin: 'bg-amber-100 text-amber-600',
  };

  return (
    <div className="py-12 md:py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-primary dark:text-white mb-2">
            {t('dashboard_welcome')}, {user?.name}!
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            {t('dashboard_title')}
          </p>
        </motion.div>

        {/* Stats cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8"
        >
          {/* Plan */}
          <div className="p-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">{t('dashboard_plan')}</p>
                <div className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-accent" />
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold capitalize ${planColors[user?.plan] || planColors.free}`}>
                    {user?.plan || 'free'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Decode count */}
          <div className="p-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">{t('dashboard_decoded')}</p>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-success" />
                  <span className="text-2xl font-bold text-slate-900 dark:text-white">
                    {stats?.decode_count || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Today's usage */}
          <div className="p-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                {language === 'nl' ? 'Vandaag gebruikt' : 'Used today'}
              </p>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                <span className="text-2xl font-bold text-slate-900 dark:text-white">
                  {usage?.today?.decodes || 0}
                </span>
                <span className="text-sm text-slate-400">
                  / {usage?.today?.decode_limit || 50}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Usage Progress Bar */}
        {usage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8 p-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-accent" />
                <span className="font-medium text-slate-900 dark:text-white">
                  {language === 'nl' ? 'Dagelijkse limiet' : 'Daily limit'}
                </span>
              </div>
              <span className="text-sm text-slate-500">
                {usage.today?.decode_remaining || 0} {language === 'nl' ? 'over' : 'remaining'}
              </span>
            </div>
            <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${
                  usage.today?.at_limit ? 'bg-error' : 
                  (usage.today?.decodes / usage.today?.decode_limit) > 0.8 ? 'bg-amber-500' : 'bg-accent'
                }`}
                style={{ width: `${Math.min(100, (usage.today?.decodes / usage.today?.decode_limit) * 100)}%` }}
              />
            </div>
            {usage.today?.at_limit && (
              <p className="mt-3 text-sm text-error">
                {language === 'nl' 
                  ? 'Je dagelijkse limiet is bereikt. Upgrade naar Pro voor meer decoderingen.'
                  : 'You have reached your daily limit. Upgrade to Pro for more decodes.'}
              </p>
            )}
          </motion.div>
        )}

        {/* Usage History Chart */}
        {usage?.history && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8 p-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700"
          >
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-accent" />
              <span className="font-medium text-slate-900 dark:text-white">
                {language === 'nl' ? 'Gebruik afgelopen 7 dagen' : 'Usage last 7 days'}
              </span>
            </div>
            <div className="flex items-end gap-2 h-24">
              {usage.history.slice().reverse().map((day, idx) => {
                const maxDecodes = Math.max(...usage.history.map(d => d.decodes), 1);
                const height = (day.decodes / maxDecodes) * 100;
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                    <div 
                      className="w-full bg-accent/20 rounded-t relative group cursor-pointer"
                      style={{ height: `${Math.max(height, 4)}%` }}
                    >
                      <div 
                        className="absolute bottom-0 w-full bg-accent rounded-t transition-all"
                        style={{ height: `${height}%` }}
                      />
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {day.decodes} {language === 'nl' ? 'decoderingen' : 'decodes'}
                      </div>
                    </div>
                    <span className="text-xs text-slate-400">
                      {new Date(day.date).toLocaleDateString(language === 'nl' ? 'nl-NL' : 'en-US', { weekday: 'short' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Quick decode */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-6"
        >
          <h2 className="font-heading font-semibold text-xl text-slate-900 dark:text-white mb-6 flex items-center gap-3">
            <Calculator className="w-5 h-5 text-accent" />
            {t('dashboard_quick_decode')}
          </h2>

          <form onSubmit={handleDecode} className="mb-6">
            <div className="flex gap-3">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(formatKenmerk(e.target.value))}
                placeholder={t('hero_placeholder')}
                className="flex-1 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white font-mono focus:border-accent focus:ring-2 focus:ring-accent/20"
                maxLength={19}
                data-testid="dashboard-decode-input"
              />
              <button
                type="submit"
                disabled={loading || cleanKenmerk(input).length !== 16}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary dark:bg-accent text-white font-medium hover:bg-slate-800 dark:hover:bg-accent-hover disabled:opacity-50 transition-colors btn-press"
                data-testid="dashboard-decode-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Search className="w-5 h-5" />
                    {t('hero_button')}
                  </>
                )}
              </button>
            </div>
          </form>

          {result && (
            <ResultCard result={result} onCopy={handleCopy} copied={copied} />
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardPage;
